import { useMemo, useState } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import styles from "./SelectMultipleItemSheet.module.css";

type MultipleItem<T extends string = string> = {
  label: string;
  value: T;
};

type SelectMultipleItemSheetProps<T extends string> = {
  items: MultipleItem<T>[];
  selectedValues: T[];
  title: string;
  visible: boolean;
  emphasised?: boolean;
  maxItems?: number;
  maxItemsWarningKey?: string;
  search?: boolean;
  label?: string;
  onSelectedValuesChange: (values: T[]) => void;
  onClose: () => void;
  onConfirmClick: () => void;
};

function SelectMultipleItemSheet<T extends string>({
  items,
  selectedValues,
  title,
  visible,
  emphasised = false,
  maxItems,
  maxItemsWarningKey = "maxSelectionString",
  search = false,
  label,
  onSelectedValuesChange,
  onClose,
  onConfirmClick,
}: SelectMultipleItemSheetProps<T>) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(query));
  }, [items, searchQuery]);

  const clearSearch = () => setSearchQuery("");

  const isOverLimit =
    maxItems !== undefined && selectedValues.length > maxItems;

  const handleItemToggle = (value: T) => {
    if (selectedValues.includes(value)) {
      onSelectedValuesChange(selectedValues.filter((v) => v !== value));
    } else {
      onSelectedValuesChange([...selectedValues, value]);
    }
  };

  return (
    <FixaModal
      visible={visible}
      handleCloseBtn={onClose}
      data-testid="SelectMultipleItem-FixaModal"
    >
      <FixaSheets
        labelledById="task-management-select-multiple-item-header"
        header={
          <FixaModalHeader
            id="task-management-select-multiple-item-header"
            title={title}
            closeBtnClick={onClose}
          />
        }
        footer={
          <FixaModalFooter renderBorder>
            <div className={styles["footerContent"]}>
              {isOverLimit && (
                <div className={styles["maxItemsWarning"]}>
                  {t(maxItemsWarningKey, { max: maxItems })}
                </div>
              )}
              <FixaButton
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={onConfirmClick}
                disabled={selectedValues.length === 0 || isOverLimit}
              />
            </div>
          </FixaModalFooter>
        }
        fullSize
      >
        <FixaModalBody>
          {search && (
            <div className={styles["searchContainer"]}>
              <FixaSearch
                id="select-multiple-item-search"
                placeholder={t("searchBasicString")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={clearSearch}
              />
            </div>
          )}
          {label && (
            <p className={styles["selectionLabel"]}>
              {maxItems === undefined ? t(label) : t(label, { max: maxItems })}
            </p>
          )}
          <FixaListView id="SelectMultipleItem-ListView">
            {filteredItems.map((item) => (
              <FixaListViewItem
                key={item.value}
                title={item.label}
                onChange={() => handleItemToggle(item.value)}
                control="checkbox"
                emphasised={emphasised}
                controlProps={{
                  checked: selectedValues.includes(item.value),
                  value: item.value,
                }}
              />
            ))}
          </FixaListView>
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
}

export default SelectMultipleItemSheet;
